import { useState, useEffect, useMemo } from "react";
import { loadFromSupabase } from "../supabaseDB";
import { computeRealHolidayImpact } from "./holidayImpactCalc";

/* GFI Nexus — Holiday Impact (fix-dashboard.md "Fase C.3", real data
   2026-09-15 per Fase B of "FOB PRICE FORECAST v2")
   ─────────────────────────────────────────────────────────────────
   Impact factors (spike/dip %) now come from REAL historical averages
   (holidayImpactCalc.js's computeRealHolidayImpact, event-study style
   vs a nearby "shoulder" baseline — see that file's header) computed
   from the 7-year USDA historical_prices backfill, whenever a holiday×
   product combination has at least 2 years of usable data on both
   sides. This screen originally shipped with hand-guessed PLACEHOLDER
   magnitudes (still kept as PLACEHOLDER_IMPACT below) explicitly
   flagged as temporary — that trigger condition ("once the Market News
   API is active" + enough history) is now true, so real numbers are
   used wherever they exist; the placeholder only remains as a fallback
   for holiday×product pairs with too little real data (e.g. Juneteenth
   before its 2021 federal recognition, or gaps in USDA's own reporting
   — see impactFor() below). */

var HOLIDAYS = [
  { key: "new_years", nameEn: "New Year's Day", nameEs: "Año Nuevo", dates: ["2026-01-01", "2027-01-01"] },
  { key: "mlk", nameEn: "Martin Luther King Jr. Day", nameEs: "Día de Martin Luther King Jr.", dates: ["2026-01-19", "2027-01-18"] },
  { key: "presidents", nameEn: "Presidents Day", nameEs: "Día de los Presidentes", dates: ["2026-02-16", "2027-02-15"] },
  { key: "memorial", nameEn: "Memorial Day", nameEs: "Memorial Day", dates: ["2026-05-25", "2027-05-31"] },
  { key: "juneteenth", nameEn: "Juneteenth", nameEs: "Juneteenth", dates: ["2026-06-19", "2027-06-19"] },
  { key: "independence", nameEn: "Independence Day", nameEs: "Día de la Independencia (EE.UU.)", dates: ["2026-07-04", "2027-07-04"] },
  { key: "labor", nameEn: "Labor Day", nameEs: "Labor Day", dates: ["2026-09-07", "2027-09-06"] },
  { key: "columbus", nameEn: "Columbus Day", nameEs: "Columbus Day", dates: ["2026-10-12", "2027-10-11"] },
  { key: "veterans", nameEn: "Veterans Day", nameEs: "Día de los Veteranos", dates: ["2026-11-11", "2027-11-11"] },
  { key: "thanksgiving", nameEn: "Thanksgiving", nameEs: "Día de Acción de Gracias", dates: ["2026-11-26", "2027-11-25"] },
  { key: "christmas", nameEn: "Christmas", nameEs: "Navidad", dates: ["2026-12-25", "2027-12-25"] },
];

/* Overall demand-impact magnitude per holiday (0-25 scale, placeholder
   estimate) — Thanksgiving/Christmas/July 4th/Memorial Day are the
   big berry-demand spikes (baking, entertaining, cookouts); federal-
   only observances like Columbus/Veterans Day barely move retail
   produce demand. */
var PLACEHOLDER_MAGNITUDE = {
  new_years: 5, mlk: 3, presidents: 3, memorial: 12, juneteenth: 6,
  independence: 14, labor: 10, columbus: 4, veterans: 3, thanksgiving: 22, christmas: 18,
};
var VARIETIES = ["blackberry", "blueberry", "raspberry"];
var VARIETY_MULTIPLIER = { blackberry: 1.0, blueberry: 1.15, raspberry: 0.9 };
var VARIETY_LABEL = { blackberry: { en: "Blackberry", es: "Zarzamora" }, blueberry: { en: "Blueberry", es: "Arándano" }, raspberry: { en: "Raspberry", es: "Frambuesa" } };
/* computeRealHolidayImpact() keys its result by historical_prices'
   product field ("Blackberry"/"Blueberry"/"Raspberry"), not this
   screen's lowercase VARIETIES keys — mapped here rather than changing
   either side's existing naming convention. */
var VARIETY_TO_PRODUCT = { blackberry: "Blackberry", blueberry: "Blueberry", raspberry: "Raspberry" };

function placeholderImpactFor(holidayKey, variety) {
  var mag = PLACEHOLDER_MAGNITUDE[holidayKey] || 5;
  var mult = VARIETY_MULTIPLIER[variety] || 1;
  return {
    spikePct: Math.round(mag * mult),
    dipPct: -Math.round(mag * 0.6 * mult),
    isReal: false,
  };
}

/* realByVariety: { blackberry: { thanksgiving: {spikePct, dipPct,
   yearsUsed, isReal}, ... }, ... } — real data wins whenever it clears
   the >=2-years-of-usable-data bar; otherwise the original placeholder
   estimate is used unchanged, so this never REGRESSES a number the
   user already saw, it only upgrades some of them. */
function impactFor(realByVariety, holidayKey, variety) {
  var real = realByVariety && realByVariety[variety] && realByVariety[variety][holidayKey];
  if (real && real.isReal && real.spikePct != null && real.dipPct != null) {
    return { spikePct: Math.round(real.spikePct), dipPct: Math.round(real.dipPct), isReal: true, yearsUsed: real.yearsUsed };
  }
  return placeholderImpactFor(holidayKey, variety);
}

var T = {
  en: {
    title: "Holiday Impact", subtitle: "US Federal Holiday calendar with estimated price impact per variety.",
    daysUntil: "days until", today: "Today!", next3: "Upcoming", allHolidays: "All Holidays (2026-2027)",
    preHoliday: "Pre-holiday (-14 to -1 days)", postHoliday: "Post-holiday (0 to +14 days)",
    spike: "spike", dip: "dip",
    note: "● marked chips use real historical averages (USDA, 7-year backfill) — vs. a nearby non-holiday baseline, not a naive year average. Unmarked chips are still estimated placeholders for holiday/variety pairs without enough real data yet (e.g. Juneteenth before 2021, or years with reporting gaps).",
    realDataTitle: "Real historical average ({years} years of USDA data)",
    estimateTitle: "Estimated placeholder — not enough real historical data yet for this holiday/variety",
  },
  es: {
    title: "Impacto de Feriados", subtitle: "Calendario de feriados federales de EE.UU. con impacto de precio estimado por variedad.",
    daysUntil: "días para", today: "¡Hoy!", next3: "Próximos", allHolidays: "Todos los Feriados (2026-2027)",
    preHoliday: "Pre-feriado (-14 a -1 días)", postHoliday: "Post-feriado (0 a +14 días)",
    spike: "spike", dip: "dip",
    note: "Los chips marcados con ● usan promedios históricos reales (USDA, backfill de 7 años) — comparados contra semanas cercanas sin feriado, no un promedio anual ingenuo. Los chips sin marcar siguen siendo estimaciones para combinaciones feriado/variedad sin suficiente historial real todavía (ej. Juneteenth antes de 2021, o años con huecos de reporte).",
    realDataTitle: "Promedio histórico real ({years} años de datos USDA)",
    estimateTitle: "Estimación — todavía no hay suficiente historial real para este feriado/variedad",
  },
};

function todayStr() { return new Date().toISOString().substring(0, 10); }

function flatUpcoming() {
  var today = todayStr();
  var flat = [];
  HOLIDAYS.forEach(function (h) {
    h.dates.forEach(function (d) { flat.push({ key: h.key, nameEn: h.nameEn, nameEs: h.nameEs, date: d }); });
  });
  flat = flat.filter(function (h) { return h.date >= today; });
  flat.sort(function (a, b) { return a.date < b.date ? -1 : 1; });
  return flat;
}

function daysUntil(dateStr) {
  var today = new Date(todayStr() + "T00:00:00");
  var target = new Date(dateStr + "T00:00:00");
  return Math.round((target - today) / 86400000);
}

function fmtDateLong(dateStr, lang) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

function addDays(dateStr, delta) {
  var d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return d.toISOString().substring(0, 10);
}

/* Compact day+month (fix-dashboard.md): "12 nov" (es) / "Nov 12" (en).
   toLocaleDateString's "short" month sometimes appends a period
   (es-MX: "nov.") — stripped so the format matches the spec exactly. */
function fmtDateCompact(dateStr, lang) {
  var d = new Date(dateStr + "T00:00:00");
  var day = d.getDate();
  var month = d.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { month: "short" }).replace(".", "");
  if (lang === "es") return day + " " + month.toLowerCase();
  return month.charAt(0).toUpperCase() + month.slice(1).toLowerCase() + " " + day;
}

/* Pre-holiday window: 14 days before through the day before. Post-
   holiday window: the holiday itself through 14 days after. Matches
   T.preHoliday/T.postHoliday's existing "-14 to -1" / "0 to +14"
   day-offset labels — this just resolves those offsets to real dates. */
function holidayWindows(dateStr, lang) {
  return {
    preRange: fmtDateCompact(addDays(dateStr, -14), lang) + " – " + fmtDateCompact(addDays(dateStr, -1), lang),
    postRange: fmtDateCompact(dateStr, lang) + " – " + fmtDateCompact(addDays(dateStr, 14), lang),
  };
}

function combinedPcts(realByVariety, holidayKey, field) {
  return VARIETIES.map(function (v) {
    var pct = impactFor(realByVariety, holidayKey, v)[field];
    return (pct > 0 ? "+" : "") + pct + "%";
  }).join("/");
}

function HolidayWindows({ holidayKey, date, lang, realByVariety }) {
  var w = holidayWindows(date, lang);
  var t = T[lang === "es" ? "es" : "en"];
  return (
    <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 4, lineHeight: 1.6 }}>
      <div>Pre: {w.preRange} <span style={{ color: "#059669", fontWeight: 700 }}>({t.spike} {combinedPcts(realByVariety, holidayKey, "spikePct")})</span></div>
      <div>Post: {w.postRange} <span style={{ color: "#DC2626", fontWeight: 700 }}>({t.dip} {combinedPcts(realByVariety, holidayKey, "dipPct")})</span></div>
    </div>
  );
}

function ImpactChips({ holidayKey, lang, realByVariety }) {
  var t = T[lang === "es" ? "es" : "en"];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
      {VARIETIES.map(function (v) {
        var imp = impactFor(realByVariety, holidayKey, v);
        var label = VARIETY_LABEL[v][lang === "es" ? "es" : "en"];
        return (
          <span key={v} title={imp.isReal ? t.realDataTitle.replace("{years}", imp.yearsUsed) : t.estimateTitle} style={{ padding: "3px 8px", borderRadius: 8, fontSize: 9.5, fontWeight: 600, background: "var(--color-bg)", border: "1px solid " + (imp.isReal ? "rgba(5,150,105,0.35)" : "var(--color-border)"), color: "var(--color-text-secondary)" }}>
            {imp.isReal && <span style={{ color: "#059669" }}>●</span>} {label}: <span style={{ color: "#059669", fontWeight: 700 }}>{imp.spikePct > 0 ? "+" : ""}{imp.spikePct}%</span> / <span style={{ color: "#DC2626", fontWeight: 700 }}>{imp.dipPct > 0 ? "+" : ""}{imp.dipPct}%</span>
          </span>
        );
      })}
    </div>
  );
}

export default function HolidayImpactRegistry({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang === "es" ? "es" : "en"];
  var [showAll, setShowAll] = useState(false);
  var [hpRows, setHpRows] = useState([]);

  useEffect(function () {
    loadFromSupabase("historical_prices").then(function (rows) { setHpRows(rows || []); }).catch(function () { setHpRows([]); });
  }, []);

  /* USDA only — SNIIM only covers Blackberry (see sniim-ingest.js), so
     mixing sources here would silently starve Blueberry/Raspberry of
     any real data while also risking a currency mismatch if SNIIM rows
     ever got included by accident. */
  var usdaRows = useMemo(function () { return hpRows.filter(function (r) { return r.source === "USDA"; }); }, [hpRows]);

  var realByVariety = useMemo(function () {
    var out = {};
    VARIETIES.forEach(function (v) {
      out[v] = computeRealHolidayImpact(usdaRows, VARIETY_TO_PRODUCT[v]);
    });
    return out;
  }, [usdaRows]);

  var upcoming = flatUpcoming();
  var next = upcoming[0];
  var nextThree = upcoming.slice(0, 3);

  return (
    <div style={{ maxHeight: "calc(100vh - 120px)", overflowY: "auto", paddingRight: 4 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
      <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: "0 0 14px" }}>{t.subtitle}</p>

      {next && (
        <div style={{ background: "linear-gradient(135deg, #1B3A4B, #2A7A6F)", borderRadius: 10, padding: 20, marginBottom: 16, color: "#fff", textAlign: "center" }}>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4 }}>{lang === "es" ? next.nameEs : next.nameEn}</div>
          <div style={{ fontSize: 34, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
            {daysUntil(next.date) === 0 ? t.today : daysUntil(next.date) + " " + t.daysUntil}
          </div>
          <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>{fmtDateLong(next.date, lang)}</div>
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>{t.next3}</div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, overflowX: "auto" }}>
        {nextThree.map(function (h, i) {
          return (
            <div key={i} style={{ flex: "1 0 200px", background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{lang === "es" ? h.nameEs : h.nameEn}</div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 4 }}>{fmtDateLong(h.date, lang)}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--color-accent)", fontFamily: "'JetBrains Mono', monospace" }}>{daysUntil(h.date)}d</div>
              <HolidayWindows holidayKey={h.key} date={h.date} lang={lang} realByVariety={realByVariety} />
              <ImpactChips holidayKey={h.key} lang={lang} realByVariety={realByVariety} />
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={function () { setShowAll(!showAll); }}
        style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600, marginBottom: 12 }}
      >
        {t.allHolidays} {showAll ? "▲" : "▼"}
      </button>

      {showAll && (
        <div style={{ marginBottom: 16 }}>
          {upcoming.map(function (h, i) {
            return (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid var(--color-border)" }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{lang === "es" ? h.nameEs : h.nameEn}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{fmtDateLong(h.date, lang)} · {daysUntil(h.date)}d</div>
                  <HolidayWindows holidayKey={h.key} date={h.date} lang={lang} realByVariety={realByVariety} />
                  <ImpactChips holidayKey={h.key} lang={lang} realByVariety={realByVariety} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(107,114,128,0.08)", border: "1px solid var(--color-border)", fontSize: 10.5, color: "var(--color-text-muted)" }}>
        {t.note}
      </div>
    </div>
  );
}
